import React, {type ComponentProps} from 'react';

import {Check, Loader2} from 'lucide-react';
import {motion} from 'motion/react';

import {cn} from '@shared/lib/utils';
import {type buttonVariants, Button} from '@shared/ui/shadcn/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/ui/shadcn/tooltip';

import type {VariantProps} from 'class-variance-authority';

type ButtonProps = ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

/**
 * Пропсы для компонента ConfirmActionButton.
 *
 * Этот компонент предоставляет механизм двухэтапного подтверждения для действий, таких как удаление или сохранение.
 * Он отображает начальный контент, и при первом клике переключается в состояние подтверждения.
 * Если confirmTooltipContent предоставлен, тултип появляется в состоянии подтверждения.
 * Состояние подтверждения сбрасывается после confirmTimeoutMs или при уходе мыши.
 *
 * @example
 * <ConfirmActionButton
 *   initialContent="Удалить"
 *   confirmContent={<Check className="size-4" />}
 *   confirmTooltipContent="Нажмите ещё раз, чтобы удалить чат"
 *   onConfirm={() => console.log('Удалено')}
 *   variant="destructive"
 * />
 */
type ConfirmActionButtonProps = {
  /** Контент, отображаемый в начальном состоянии. */
  initialContent: React.ReactNode;
  /** Контент, отображаемый в состоянии подтверждения. */
  confirmContent?: React.ReactNode;
  /** Опциональный текст тултипа, показываемый в состоянии подтверждения. */
  confirmTooltipContent?: string;
  /** Таймаут в миллисекундах для сброса в начальное состояние (по умолчанию: 3000). */
  confirmTimeoutMs?: number;
  /** Колбэк, выполняемый при подтверждении действия. */
  onConfirm: () => void | Promise<void>;
  /** Колбэк, выполняемый при отмене подтверждения (например, таймаут или уход мыши). */
  onCancel?: () => void;
  /** Вариант кнопки для начального состояния. */
  variant?: ButtonProps['variant'];
  /** Вариант кнопки для состояния подтверждения. */
  confirmVariant?: ButtonProps['variant'];
  /** Размер кнопки. */
  size?: ButtonProps['size'];
  /** Дополнительные CSS классы. */
  className?: string;
  /** Отключена ли кнопка. */
  disabled?: boolean;
  /** Находится ли кнопка в состоянии загрузки. */
  loading?: boolean;
  /** Доступное название для кнопки, содержащей только иконку. */
  ariaLabel?: string;
};

export const ConfirmActionButton = ({
  initialContent,
  confirmContent = <Check className='size-4' />,
  confirmTooltipContent,
  confirmTimeoutMs = 3000,
  onConfirm,
  onCancel,
  variant = 'ghost',
  confirmVariant = 'destructive',
  size = 'sm',
  className,
  disabled,
  loading = false,
  ariaLabel,
}: ConfirmActionButtonProps) => {
  const [confirming, setConfirming] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);

  const resetConfirming = React.useCallback(() => {
    setConfirming(false);
    onCancel?.();
  }, [onCancel]);

  React.useEffect(() => {
    if (!confirming) return;
    const t = window.setTimeout(() => resetConfirming(), confirmTimeoutMs);
    return () => window.clearTimeout(t);
  }, [confirming, confirmTimeoutMs, resetConfirming]);

  const handleClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (isLoading) return;
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleClick(e as any);
    }
  };

  const buttonContent = React.useMemo(() => {
    if (loading || isLoading) {
      return <Loader2 className='size-4 animate-spin' />;
    }
    return confirming ? confirmContent : initialContent;
  }, [loading, isLoading, confirming, confirmContent, initialContent]);

  const button = (
    <Button
      type='button'
      size={size}
      variant={confirming ? confirmVariant : variant}
      className={cn('cursor-pointer', className)}
      disabled={disabled || loading || isLoading}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseLeave={resetConfirming}
      aria-label={
        confirming
          ? `Подтвердить: ${ariaLabel ?? (typeof initialContent === 'string' ? initialContent : 'действие')}`
          : (ariaLabel ??
            (typeof initialContent === 'string'
              ? initialContent
              : 'Кнопка действия'))
      }
      aria-pressed={confirming}
    >
      <motion.span
        key={confirming ? 'confirm' : 'idle'}
        className='inline-flex items-center gap-1.5'
        initial={{opacity: 0, scale: 0.8}}
        animate={{opacity: 1, scale: 1}}
        exit={{opacity: 0, scale: 0.8}}
        transition={{duration: 0.2}}
      >
        {buttonContent}
      </motion.span>
    </Button>
  );

  if (confirmTooltipContent && confirming) {
    return (
      <TooltipProvider>
        <Tooltip open={confirming}>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent>
            <p>{confirmTooltipContent}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return button;
};
